<template>
  <div class="images-container">
    <el-card class="images-header-card">
      <div class="header-content">
        <div class="header-left">
          <h2>图片库管理</h2>
          <div class="store-info">
            <span class="store-name">{{ storeName }}</span>
            <el-tag type="info" size="small">共 {{ totalImages }} 张图片</el-tag>
          </div>
        </div>
        <div class="header-right">
          <el-button type="primary" @click="openUploader">
            <i class="el-icon-upload"></i>
            上传图片
          </el-button>
          <el-button @click="refreshData">
            <i class="el-icon-refresh"></i>
            刷新
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 搜索 -->
    <el-card class="filter-card">
      <div class="filter-content">
        <el-input
            v-model="searchKeyword"
            placeholder="搜索图片名称/别名"
            prefix-icon="el-icon-search"
            style="width: 250px;"
            @keyup.enter.native="handleSearch"
            clearable
            @clear="handleSearch"
        />
      </div>
    </el-card>

    <!-- 图片列表 -->
    <el-card class="images-list-card">
      <div v-loading="loading">
        <div class="images-grid" v-if="imageList.length > 0">
          <div
              v-for="image in imageList"
              :key="image.id"
              class="image-item"
              @click="previewImage(image)"
          >
            <div class="image-preview">
              <img :src="image.url" :alt="image.alias || image.filename" @error="handleImageError"/>
            </div>
            <div class="image-info">
              <div class="image-name" :title="image.alias || image.filename">
                {{ image.alias || image.filename || '未命名' }}
              </div>
              <div class="image-meta">
                <span class="image-size">{{ formatFileSize(image.size) }}</span>
                <span class="image-filename">{{ image.filename }}</span>
              </div>
            </div>
            <div class="image-actions">
              <el-button type="text" size="small" @click.stop="copyImageUrl(image.url)">
                <i class="el-icon-link"></i>
                复制链接
              </el-button>
              <el-button
                  type="text"
                  size="small"
                  style="color: #E6A23C"
                  @click.stop="handleEditAlias(image)"
              >
                <i class="el-icon-edit"></i>
                编辑别名
              </el-button>
              <el-button
                  type="text"
                  size="small"
                  style="color: #F56C6C"
                  @click.stop="deleteImage(image)"
              >
                <i class="el-icon-delete"></i>
                删除
              </el-button>
            </div>
          </div>
        </div>

        <el-empty v-else description="暂无图片，请点击上传" />

        <!-- 分页 -->
        <div class="pagination-container" v-if="totalImages > 0">
          <el-pagination
              :current-page="pagination.currentPage"
              :page-sizes="[24, 48, 96]"
              :page-size="pagination.pageSize"
              :total="totalImages"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 图片预览对话框 -->
    <el-dialog
        :visible.sync="previewDialog.visible"
        width="800px"
        append-to-body
        @closed="handlePreviewClosed"
    >
      <div class="preview-container">
        <img :src="previewDialog.image.url" class="preview-image" @error="handleImageError"/>
        <el-descriptions :column="2" border class="preview-info">
          <el-descriptions-item label="图片ID">{{ previewDialog.image.id }}</el-descriptions-item>
          <el-descriptions-item label="图片别名">{{ previewDialog.image.alias || '--' }}</el-descriptions-item>
          <el-descriptions-item label="原始文件名">{{ previewDialog.image.filename || '--' }}</el-descriptions-item>
          <el-descriptions-item label="文件大小">{{ formatFileSize(previewDialog.image.size) }}</el-descriptions-item>
          <el-descriptions-item label="图片链接" :span="2">
            <el-input :value="previewDialog.image.url" readonly size="small" class="url-input" />
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="previewDialog.visible = false">关闭</el-button>
        <el-button type="primary" @click="copyImageUrl(previewDialog.image.url)">
          复制链接
        </el-button>
      </span>
    </el-dialog>

    <!-- 编辑别名对话框 -->
    <el-dialog
        title="编辑图片别名"
        :visible.sync="editAliasDialog.visible"
        width="400px"
        append-to-body
    >
      <el-form :model="editAliasDialog.form" label-width="80px">
        <el-form-item label="图片别名">
          <el-input
              v-model="editAliasDialog.form.alias"
              placeholder="请输入图片别名"
              maxlength="255"
              show-word-limit
          />
        </el-form-item>
        <div class="form-tip">原始文件名：{{ editAliasDialog.originalFilename }}</div>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editAliasDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveAlias" :loading="editAliasDialog.loading">
          确认保存
        </el-button>
      </span>
    </el-dialog>

    <!-- 图片上传器 -->
    <image-uploader
        :visible.sync="uploaderVisible"
        @upload="handleUploadComplete"
        :category="currentCategory"
        @close="handleUploaderClose"
    />
  </div>
</template>

<script>
import api from "@/utils/api"
import ImageUploader from "@/components/ImageUploader"

export default {
  name: 'StoreImages',
  components: {
    ImageUploader
  },
  data() {
    return {
      loading: false,
      storeId: null,
      storeName: '',

      searchKeyword: '',

      imageList: [],
      totalImages: 0,

      pagination: {
        currentPage: 1,
        pageSize: 24
      },

      previewDialog: {
        visible: false,
        image: {}
      },

      editAliasDialog: {
        visible: false,
        loading: false,
        imageId: null,
        originalFilename: '',
        form: {
          alias: ''
        }
      },

      currentCategory: null,
      uploaderVisible: false
    }
  },

  mounted() {
    this.initData()
  },

  methods: {
    async initData() {
      console.log('=== 初始化店铺图片库 ===')

      try {
        await this.getCurrentSellerInfo()
        await this.loadImageList()
      } catch (error) {
        console.error('初始化失败:', error)
      }
    },

    async getCurrentSellerInfo() {
      try {
        const res = await api.login_getSellerInfo(null, {})
        const responseData = res.data || res
        if (responseData.code === '000' && responseData.data) {
          this.storeId = responseData.data.merchantId || null
          this.storeName = responseData.data.storeName || responseData.data.name || '我的店铺'
          console.log('当前店铺ID:', this.storeId)
          console.log('当前店铺名称:', this.storeName)
        }
      } catch (error) {
        console.error('获取员工信息异常:', error)
      }
    },

    async loadImageList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize
        }

        // 添加搜索关键词
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }

        // 注意：不需要传 merchantId，后端通过 SessionUtil.getAdminId() 自动获取当前用户权限
        // 后端会根据自己的逻辑判断返回哪些图片（只返回当前店铺的图片）

        console.log('请求参数:', params)

        const res = await api.image_getImageList(params)
        console.log('图片列表响应:', res)

        const responseData = res.data || res
        if (responseData.code === '000' && responseData.data) {
          this.imageList = responseData.data.list || []
          this.totalImages = responseData.data.totalCount || 0
          console.log(`加载成功: ${this.imageList.length} 张图片, 总计 ${this.totalImages} 张`)
        } else {
          console.error('接口返回错误:', responseData)
          this.$message.error(responseData.message || '加载图片列表失败')
          this.imageList = []
          this.totalImages = 0
        }
      } catch (error) {
        console.error('加载图片列表失败:', error)
        this.$message.error('加载图片列表失败')
        this.imageList = []
        this.totalImages = 0
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.currentPage = 1
      this.loadImageList()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.loadImageList()
    },

    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.loadImageList()
    },

    openUploader() {
      this.uploaderVisible = true
    },

    handleUploadComplete() {
      this.$message.success('上传成功')
      this.uploaderVisible = false
      this.loadImageList()
    },

    handleUploaderClose() {
      this.uploaderVisible = false
    },

    previewImage(image) {
      this.previewDialog.image = image
      this.previewDialog.visible = true
    },

    handlePreviewClosed() {
      this.previewDialog.image = {}
    },

    handleEditAlias(image) {
      this.editAliasDialog.imageId = image.id
      this.editAliasDialog.originalFilename = image.filename
      this.editAliasDialog.form.alias = image.alias || ''
      this.editAliasDialog.visible = true
    },

    async saveAlias() {
      this.editAliasDialog.loading = true
      try {
        const res = await api.image_editImage({
          id: this.editAliasDialog.imageId,
          alias: this.editAliasDialog.form.alias
        })

        const responseData = res.data || res
        if (responseData.code === '000') {
          this.$message.success('更新成功')
          this.editAliasDialog.visible = false
          this.loadImageList()
        } else {
          this.$message.error(responseData.message || '更新失败')
        }
      } catch (error) {
        console.error('保存别名失败:', error)
        this.$message.error('保存失败，请重试')
      } finally {
        this.editAliasDialog.loading = false
      }
    },

    copyImageUrl(url) {
      if (!url) {
        this.$message.warning('图片链接为空')
        return
      }
      navigator.clipboard.writeText(url)
          .then(() => {
            this.$message.success('链接已复制到剪贴板')
          })
          .catch(() => {
            this.$message.error('复制失败，请手动复制')
          })
    },

    async deleteImage(image) {
      try {
        await this.$confirm(`确定要删除图片 "${image.alias || image.filename || '未命名'}" 吗？删除后不可恢复！`, '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          confirmButtonClass: 'el-button--danger'
        })

        const res = await api.image_deleteImage({ id: image.id })
        const responseData = res.data || res
        if (responseData.code === '000') {
          this.$message.success('删除成功')
          this.loadImageList()
        } else {
          this.$message.error(responseData.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    handleImageError(e) {
      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext fill="%23999" x="50" y="55" text-anchor="middle"%3E图片加载失败%3C/text%3E%3C/svg%3E'
    },

    formatFileSize(bytes) {
      if (!bytes) return '--'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
    },

    refreshData() {
      this.pagination.currentPage = 1
      this.searchKeyword = ''
      this.loadImageList()
    }
  }
}
</script>

<style scoped lang="scss">
.images-container {
  padding: 20px;
}

.images-header-card {
  margin-bottom: 20px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-left {
    h2 {
      margin: 0 0 8px 0;
      font-size: 24px;
      color: #303133;
    }

    .store-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .store-name {
        font-size: 16px;
        color: #606266;
        font-weight: 500;
      }
    }
  }
}

.filter-card {
  margin-bottom: 20px;
}

.images-list-card {
  .images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 20px;
  }

  .image-item {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;
    background: #fff;

    &:hover {
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    .image-preview {
      height: 160px;
      overflow: hidden;
      background-color: #f5f7fa;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .image-info {
      padding: 12px;

      .image-name {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .image-meta {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #909399;

        .image-filename {
          max-width: 120px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .image-actions {
      padding: 8px 12px 12px;
      border-top: 1px solid #ebeef5;
      display: flex;
      justify-content: space-around;
    }
  }
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.preview-container {
  .preview-image {
    width: 100%;
    max-height: 400px;
    object-fit: contain;
    margin-bottom: 20px;
  }

  .preview-info {
    .url-input {
      cursor: pointer;
    }
  }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

@media (max-width: 768px) {
  .images-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>